import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHero } from "@/components/site/SiteBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BRANCHES = [
  { city: "Cape Town — Head Office", addr: "12 Dock Road, V&A Waterfront, Cape Town", phone: "+27 21 555 0100" },
  { city: "Johannesburg — National Hub", addr: "45 Logistics Park, City Deep, Johannesburg", phone: "+27 11 555 0200" },
  { city: "Durban — Port Terminal", addr: "8 Harbour Way, Maydon Wharf, Durban", phone: "+27 31 555 0300" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div>
      <PageHero eyebrow="Contact" title="Let's move your cargo" subtitle="Reach our teams in Cape Town, Johannesburg and Durban — or send us a message and we'll respond within one business day." />
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="text-2xl font-heading font-extrabold">Our offices</h2>
          <div className="mt-6 space-y-4">
            {BRANCHES.map((b) => (
              <div key={b.city} className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-heading font-bold flex items-center gap-2"><MapPin size={18} className="text-primary" /> {b.city}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{b.addr}</p>
                <p className="mt-1 text-sm flex items-center gap-2"><Phone size={14} className="text-primary" /> {b.phone}</p>
              </div>
            ))}
            <div className="flex flex-wrap gap-6 pt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><Mail size={16} className="text-primary" /> hello@ebiklogistics.com</span>
              <span className="flex items-center gap-2"><Clock size={16} className="text-primary" /> Control Tower 24/7</span>
            </div>
          </div>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 lg:p-8 space-y-4 h-fit">
          <h2 className="text-xl font-heading font-extrabold">Send us a message</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Name</Label><Input data-testid="contact-name" value={form.name} onChange={set("name")} required className="mt-1.5" /></div>
            <div><Label>Company</Label><Input data-testid="contact-company" value={form.company} onChange={set("company")} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
          </div>
          <div><Label>Message</Label><Textarea data-testid="contact-message" value={form.message} onChange={set("message")} required rows={5} className="mt-1.5" /></div>
          <Button data-testid="contact-submit" type="submit" disabled={loading} className="w-full bg-primary hover:bg-red-700 h-11">
            {loading ? "Sending…" : "Send Message"}
          </Button>
        </form>
      </section>
    </div>
  );
}
